import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { BRANDING_ELIGIBLE_PLAN_IDS } from '../utils/branding';
import CompanyBrandingEditor from './CompanyBrandingEditor';

/**
 * Company accent / branding: full editor for Pro/Pro+, or upgrade explainer for other plans.
 * Opened from the Home plan summary bar.
 */
export default function CompanyBrandingModal({ open, onClose, onOpenChangePlan, onBrandingSaved }) {
  const { user } = useAuth();
  const { company, loading } = useBranding();

  if (!open) return null;

  const ownerIds = Array.isArray(company?.accountOwnerIds)
    ? company.accountOwnerIds
    : company?.accountOwnerId
      ? [company.accountOwnerId]
      : [];
  const isAccountOwner = user?.uid && ownerIds.includes(user.uid);
  const planId = company?.planId || '';
  const isEligiblePlan = BRANDING_ELIGIBLE_PLAN_IDS.includes(planId);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="company-branding-modal-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: 20,
        overflow: 'auto',
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: 'var(--branding-app-surface)',
          borderRadius: 16,
          padding: 24,
          maxWidth: 1040,
          width: '100%',
          maxHeight: 'min(92vh, 900px)',
          overflow: 'auto',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {loading && !company ? (
          <p style={{ color: 'var(--cc-text-muted)', margin: 0 }}>Loading…</p>
        ) : !company ? (
          <div>
            <p style={{ color: 'var(--cc-text-muted)' }}>Company not found.</p>
            <button type="button" onClick={onClose} style={{ marginTop: 12, padding: '8px 16px', borderRadius: 8, border: '1px solid var(--branding-app-surface-panel-border)', background: 'var(--cc-card-bg)', cursor: 'pointer' }}>
              Close
            </button>
          </div>
        ) : !isAccountOwner ? (
          <div>
            <h2 id="company-branding-modal-title" style={{ fontSize: 18, color: 'var(--cc-text-primary)', margin: '0 0 8px' }}>
              Company branding
            </h2>
            <p style={{ color: 'var(--cc-text-muted)' }}>Only account owners can change company branding.</p>
            <button type="button" onClick={onClose} style={{ marginTop: 16, padding: '8px 16px', borderRadius: 8, border: '1px solid var(--branding-app-surface-panel-border)', background: 'var(--cc-card-bg)', cursor: 'pointer' }}>
              Close
            </button>
          </div>
        ) : !isEligiblePlan ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
              <h2 id="company-branding-modal-title" style={{ fontSize: 18, fontWeight: 700, color: 'var(--cc-text-primary)', margin: 0 }}>
                Accent colour & branding
              </h2>
              <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  border: '1px solid var(--branding-app-surface-panel-border)',
                  background: 'var(--cc-card-bg)',
                  cursor: 'pointer',
                  fontSize: 20,
                  lineHeight: 1,
                  color: 'var(--cc-text-muted)',
                }}
              >
                ×
              </button>
            </div>
            <p style={{ color: 'var(--cc-text-secondary)', lineHeight: 1.6, margin: '0 0 16px' }}>
              Custom <strong>accent colours</strong> (buttons, links, and the Home plan strip) are available on{' '}
              <strong>Pro</strong> and <strong>Pro+</strong>. Upgrade to unlock branding for your whole company.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {typeof onOpenChangePlan === 'function' && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onOpenChangePlan();
                  }}
                  style={{
                    padding: '10px 18px',
                    borderRadius: 8,
                    border: 'none',
                    background: 'var(--branding-app-primary)',
                    color: 'var(--branding-app-primary-fg)',
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: 'pointer',
                  }}
                >
                  Change plan & seats
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '10px 18px',
                  borderRadius: 8,
                  border: '1px solid var(--branding-app-surface-panel-border)',
                  background: 'var(--cc-card-bg)',
                  color: 'var(--cc-text-secondary)',
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <CompanyBrandingEditor compact onClose={onClose} onSaved={onBrandingSaved} />
        )}
      </div>
    </div>
  );
}
